import i18n from '@link/share/i18n';
import { hasGtmDataLayer, pushGtmEvent } from '../gtm';
import type {
  Ga4AccountDataLayerEvent,
  Ga4DataLayerEvent,
  Ga4Ecommerce,
  Ga4EcommerceEventName
} from './types';

/** Upper bound on how long a caller waits for GTM before moving on. */
const GTM_EVENT_TIMEOUT_MS = 500;

/** Every GA4 param except the two each push sets itself. */
type Ga4ResetParam = Exclude<
  keyof Ga4DataLayerEvent | keyof Ga4AccountDataLayerEvent,
  'event' | 'ui_language'
>;

/**
 * Cleared before every GA4 push, ecommerce and account alike, so neither kind
 * of param rides along on the other's hits. Typed so a new param cannot ship
 * without its reset.
 */
const GA4_RESET = {
  ecommerce: null,
  payment_provider: null,
  agent_id: null,
  method: null,
  account_action: null,
  email_verification_required: null,
  flow_context: null,
  entry_point: null
} satisfies Record<Ga4ResetParam, null>;

export interface PushGa4EventParams {
  event: Ga4EcommerceEventName;
  agentId: string;
  ecommerce: Ga4Ecommerce;
  paymentProvider?: 'linkpay';
  /**
   * The app's locale code. Defaults to share's i18n instance, which an app
   * running its own instance (the SSR app) never initializes.
   */
  uiLanguage?: string;
  /**
   * Called once GTM reports its tags fired, after GTM_EVENT_TIMEOUT_MS, or
   * immediately when GTM is not loaded at all. Runs exactly once, so a caller
   * can safely gate a navigation on it.
   */
  onSent?: () => void;
}

export function pushGa4Event({
  event,
  agentId,
  ecommerce,
  paymentProvider,
  uiLanguage = i18n.language,
  onSent
}: PushGa4EventParams): void {
  // Read before pushing: with no dataLayer, react-gtm-module injects an inline
  // script that both creates one and drops our eventCallback.
  const gtmCanCallBack = hasGtmDataLayer();

  // One latch for the two things that can settle: GTM's callback and the
  // timeout below. Whichever loses is a no-op, so the timer needs no clearing.
  let settled = false;
  const settle = () => {
    if (settled) return;
    settled = true;
    onSent?.();
  };

  const payload: Ga4DataLayerEvent & { eventCallback?: () => void } = {
    event,
    // The app's own locale code, which is what the spec defines ui_language as:
    // `ja` | `en` | `zh` | `zt` | `ko`. Not a BCP-47 tag.
    ui_language: uiLanguage ?? '',
    agent_id: agentId,
    ...(paymentProvider ? { payment_provider: paymentProvider } : {}),
    ecommerce,
    ...(onSent ? { eventCallback: settle } : {})
  };
  pushGtmEvent(payload, GA4_RESET);

  if (!onSent) return;
  if (!gtmCanCallBack) {
    settle();
    return;
  }
  // GTM also never calls back when gtm.js itself is blocked.
  window.setTimeout(settle, GTM_EVENT_TIMEOUT_MS);
}

/**
 * Account events need no GTM callback: every navigation after one stays in the
 * app and sign-in uses popups, so the page never unloads under it.
 */
export function pushGa4AccountEvent(payload: Ga4AccountDataLayerEvent): void {
  pushGtmEvent(payload, GA4_RESET);
}
